import { LocationIcon, CalendarIcon, UsersIcon } from "./icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCameraRetro } from "@fortawesome/free-solid-svg-icons";

const ImageGalleryGrid = ({ destinationImages, tripSummary, onImageClick }) => {
  return (
    <div className="w-full px-3 sm:px-4 lg:px-6 xl:pl-4 xl:pr-6 py-4 sm:py-6">
      <div className="sm:hidden flex flex-col h-[calc(100vh-130px)] max-h-[800px]">
        <div className="mb-4 shrink-0 px-1">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faCameraRetro} className="text-teal-600 text-2xl" /> Destination Gallery
          </h2>
          <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">
            A visual preview of your upcoming journey to {tripSummary?.destination || 'your destination'}. Tap any photo to view the full collection!
          </p>
        </div>
        <button
          onClick={() => onImageClick(0)}
          className="relative w-full flex-1 min-h-0 rounded-t-2xl overflow-hidden cursor-pointer group"
        >
          <img
            src={destinationImages?.[0]?.url || "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200"}
            alt={tripSummary?.destination}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h1 className="text-2xl font-bold text-white mb-2">
              {tripSummary?.tripTitle || `Explore ${tripSummary?.destination}`}
            </h1>
            <div className="flex flex-wrap gap-2 text-white/90">
              <span className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs">
                <LocationIcon />
                <span className="truncate max-w-[120px]">{tripSummary?.startingFrom} → {tripSummary?.destination}</span>
              </span>
              <span className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs">
                <CalendarIcon />
                {tripSummary?.duration}
              </span>
              <span className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs">
                <UsersIcon />
                {tripSummary?.travelers?.description}
              </span>
            </div>
          </div>
        </button>

        <div className="grid grid-cols-2 gap-0.5 h-[35%] min-h-[140px] shrink-0 rounded-b-2xl overflow-hidden mt-0.5">
          {destinationImages?.slice(1, 4).map((img, idx) => (
            <button
              key={idx}
              onClick={() => onImageClick(idx + 1)}
              className="relative group cursor-pointer overflow-hidden"
            >
              <img
                src={img.url}
                alt={img.alt || 'Travel photo'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </button>
          ))}
          {destinationImages?.[4] && (
            <button
              onClick={() => onImageClick(4)}
              className="relative group cursor-pointer overflow-hidden"
            >
              <img
                src={destinationImages[4].url}
                alt={destinationImages[4].alt || 'Travel photo'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {destinationImages.length > 5 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white text-lg font-bold">+{destinationImages.length - 5} more</span>
                </div>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="hidden sm:grid grid-cols-3 gap-1 h-[300px] md:h-[380px] lg:h-[420px] xl:h-[480px] 2xl:h-[520px] rounded-2xl overflow-hidden">
        <button
          onClick={() => onImageClick(0)}
          className="row-span-2 relative group cursor-pointer"
        >
          <img
            src={destinationImages?.[0]?.url || "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200"}
            alt={tripSummary?.destination}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-left">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-3 text-left">
              {tripSummary?.tripTitle || `Explore ${tripSummary?.destination}`}
            </h1>
            <div className="flex flex-wrap items-center justify-start gap-2 text-white/90">
              <span className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm">
                <LocationIcon />
                <span className="hidden md:inline">{tripSummary?.startingFrom} → {tripSummary?.destination}</span>
                <span className="md:hidden truncate max-w-[80px]">{tripSummary?.destination}</span>
              </span>
              <span className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm">
                <CalendarIcon />
                {tripSummary?.duration}
              </span>
              <span className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm">
                <UsersIcon />
                <span className="hidden md:inline">{tripSummary?.travelers?.description}</span>
                <span className="md:hidden">{tripSummary?.travelers?.description?.split(' ')[0]}</span>
              </span>
            </div>
          </div>
        </button>

        {destinationImages?.slice(1, 3).map((img, idx) => (
          <button
            key={idx}
            onClick={() => onImageClick(idx + 1)}
            className="relative group cursor-pointer overflow-hidden"
          >
            <img
              src={img.url}
              alt={img.alt || 'Travel photo'}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </button>
        ))}

        {destinationImages?.[3] && (
          <button
            onClick={() => onImageClick(3)}
            className="relative group cursor-pointer overflow-hidden"
          >
            <img
              src={destinationImages[3].url}
              alt={destinationImages[3].alt || 'Travel photo'}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </button>
        )}

        {destinationImages?.[4] && (
          <button
            onClick={() => onImageClick(4)}
            className="relative group cursor-pointer overflow-hidden"
          >
            <img
              src={destinationImages[4].url}
              alt={destinationImages[4].alt || 'Travel photo'}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            {destinationImages.length > 5 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                <span className="text-white text-xl md:text-2xl font-bold">+{destinationImages.length - 5} more</span>
              </div>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default ImageGalleryGrid;
